/**
 * Pull events from the server for a baby and merge into Dexie via
 * upsertFromServer (which never clobbers locally-pending rows).
 *
 * BabyTrack has no /dashboard aggregate endpoint, so the unit of pull is
 * one baby — callers typically pull the selected baby on app foreground.
 *
 * The pull MUST be per type. The server's unfiltered list branch queries the
 * base table (`PK = BABY#<id> AND begins_with(SK, 'EVENT#')`, descending,
 * `Limit: 50`) and the SK is `EVENT#<type>#<startTime>#<id>`, so descending
 * order is TYPE-major, not time-major: a family with 50+ sleep rows (about a
 * fortnight of normal use) gets a first page of nothing but sleeps, and every
 * Feed / Nappy / Pumping / Activity row never reaches Dexie at all. Since the
 * whole UI reads events from Dexie, a reinstall or a new device would show
 * empty history for those types. Passing `type` hits the GSI1 branch instead,
 * which is ordered by date within one type and pages properly via `nextToken`.
 */

import * as eventsRepo from '../db/events';
import { getMeta, setMeta, META_KEYS } from '../db/meta';
import { rawGet } from './network';
import { isOffline } from '../network';

const PULL_TYPES = ['FEED', 'SLEEP', 'NAPPY', 'ACTIVITY', 'PUMPING'];
const PAGE_LIMIT = 100; // server clamps to 100
const WINDOW_DAYS = 30; // how far back a backfill walks
const MAX_PAGES_PER_TYPE = 8; // hard ceiling so one type can't run away
// Between backfills we only refresh the newest page of each type — enough for
// every screen, and it keeps the 5-minute heartbeat cheap.
const FULL_PULL_EVERY_MS = 6 * 60 * 60 * 1000;

function windowCutoffDate(days) {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000)
    .toISOString()
    .substring(0, 10);
}

/** Oldest startTime in a page (rows come back newest-first, but don't rely on it). */
function oldestStartTime(rows) {
  let oldest = null;
  for (const r of rows) {
    const ts = String(r?.startTime || r?.createdAt || '');
    if (!ts) continue;
    if (oldest === null || ts < oldest) oldest = ts;
  }
  return oldest;
}

// Never throws: a page that fails (timeout, 5xx) returns what the earlier
// pages already merged, with `error` set — page 1 may have written a
// co-parent's nap, and that write is the one-shot `changed` signal the sync
// engine refreshes the schedule on; the next pull sees it as 'unchanged'.
async function pullType(babyId, type, { maxPages, cutoffDate, extra }) {
  let nextToken = null;
  let pages = 0;
  let count = 0;
  let changed = 0;

  try {
    do {
      const res = await rawGet(`/babies/${babyId}/events`, {
        ...extra,
        type,
        limit: PAGE_LIMIT,
        ...(nextToken ? { nextToken } : {}),
      });
      const rows = res?.items || res?.events || (Array.isArray(res) ? res : []) || [];

      for (const r of rows) {
        if (r?.eventId) {
          const outcome = await eventsRepo.upsertFromServer({ ...r, babyId });
          count += 1;
          if (outcome === 'inserted' || outcome === 'updated') changed += 1;
        }
      }

      pages += 1;
      // An empty page means the plan's history cap filtered the rest away
      // (`historyLimited`), so there is nothing further worth walking.
      nextToken = rows.length ? res?.nextToken || null : null;

      // Rows are date-ordered within a type: once a page reaches past the
      // window there is no reason to keep paging.
      const oldest = oldestStartTime(rows);
      if (oldest && oldest.substring(0, 10) < cutoffDate) nextToken = null;
    } while (nextToken && pages < maxPages);
  } catch (error) {
    return { count, changed, error };
  }

  return { count, changed };
}

export async function pullEventsForBaby(babyId, filters = {}) {
  if (!babyId) return { skipped: true };
  if (isOffline()) return { offline: true };

  const { type, limit, nextToken, ...extra } = filters || {};
  const types = type ? [type] : PULL_TYPES;

  const lastFullAt = Date.parse((await getMeta(META_KEYS.lastFullPullAt(babyId))) || '');
  const backfill =
    Number.isNaN(lastFullAt) || Date.now() - lastFullAt > FULL_PULL_EVERY_MS;
  const maxPages = backfill ? MAX_PAGES_PER_TYPE : 1;
  const cutoffDate = windowCutoffDate(WINDOW_DAYS);

  let count = 0;
  let changed = 0;
  let failedTypes = 0;
  let firstError = null;
  for (const t of types) {
    try {
      const res = await pullType(babyId, t, { maxPages, cutoffDate, extra });
      // Partial counts are kept even when a later page of the type failed.
      count += res.count;
      changed += res.changed;
      if (res.error) throw res.error;
    } catch (err) {
      // One type failing (timeout, 5xx) must not throw away the types that
      // already merged — the engine retries on 'online' or the heartbeat.
      failedTypes += 1;
      if (!firstError) firstError = err;
    }
  }
  if (failedTypes === types.length) throw firstError;

  const now = new Date().toISOString();
  await setMeta(META_KEYS.lastSyncAt(babyId), now);
  if (backfill && !failedTypes && !type) {
    await setMeta(META_KEYS.lastFullPullAt(babyId), now);
  }
  // `count` is every row the pull returned (the newest page of each type,
  // every time); `changed` is only the rows Dexie did not already hold in
  // that exact state — the signal for "something on the server moved".
  return { count, changed, partial: failedTypes > 0 };
}
